import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { pickBy, isEmpty } from 'lodash';
import isEqual from 'react-fast-compare';
import { withStyles  } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { TrackingCtx  } from "../tracking";
import IoToolbar from './IoToolbar';
import IoForm from './IoForm';
import ErrorPage from '../errors/ErrorPage';
import PlacementsGrid from '../containers/PlacementsGrid';
import PackagesGrid from '../containers/PackagesGrid';
import CloneIoDialog from './CloneIoDialog';
import { saveOrderAndNotify, getOrder } from '../actions/orderActions';
import { requestAcceptanceWithNotifications } from "../actions/acceptActions";
import { changeBuyerWithNotifications } from '../actions/changeBuyerActions';
import { reopenProposalWithNotifications } from "../actions/reopenActions";
import { isLiveOrder } from '../utils/adManagerHelpers';
import { validate } from '../utils/formValidator';
import { countLineItems } from '../utils/countLineItems';
import { client } from '../actions/client';
import { toast } from "react-toastify";

const styles = () => ({
  root: {
    padding: "70px 20px 20px 20px",
    overflow: "scroll",
  },
});

const POLL_INTERVAL_MS = 3000;

class IoPage extends Component {

  constructor(props) {
    super(props);
    const advertiser = this.getAdvertiser();
    const order = {
      dfpOrderId: '',
      totalOrderAmount: '',
      AdvertiserId: advertiser.id || 0,
      Advertiser: advertiser,
      agencyOrderName: '',
      IoTypeId: '',
      IoStatusId: 1,
      billingType: '',
      agencyOrderId: '',
      jagIoGroup: '',
      start: '',
      end: '',
      buyerName: '',
      buyerId: 0,
      orderType: advertiser.type,
      lock: '',
      Placements: []
    };
    this.setValidationRules(order.orderType, order.IoStatusId);
    let gridType = parseInt(localStorage.getItem('gridType'), 10);
    if (isNaN(gridType) || (gridType !== 0 && gridType !== 1 && gridType !== 2)) {
      gridType = 1;
    }
    this.state = {
      order: !isEmpty(props.order) ? props.order : order,
      advertiser: advertiser,
      cloneDialogOpen: false,
      cloneIOAgencyName: '',
      cloneIOAgencyId: (this.props.orderType === 'Programmatic') ? 'Programmatic' : '',
      cloneIOAdvertiser: advertiser,
      pushDialogOpen: false,
      ordersExpanded: true,
      gridType: gridType,
      pushInitiated: false,
      errors: {},
      user: localStorage.getItem('user'),
    };
  }

  componentWillUnmount() {
    if (this.poll) {
      clearTimeout(this.poll.timer);
      delete this.poll;
    }
  }

  async pollLockStatus() {
    const { dispatch } = this.props;
    const { order, pushInitiated } = this.state;
    const { lock } = order;
    if (!lock && !pushInitiated) {
      return;
    }
    if (this.poll) {
      return;
    }
    this.poll = {};
    let res;
    try {
      res = await client.get(`/ios/${order.id}/islocked`, { skipErrorToast: true });
    } catch (err) {
      delete this.poll;
      const errMsg = `Could not check whether order ${order.id} is still locked: ${err.message || 'unknown error'}`;
      toast.error(errMsg, { theme: 'colored', toastId: errMsg });
      return;
    }
    if (res.data.locked === true) {
      this.poll.timer = setTimeout(() => {
        delete this.poll;
        this.pollLockStatus();
      }, POLL_INTERVAL_MS);
      return;
    }
    delete this.poll;
    order.lock = undefined;
    this.setState({ order: order, pushInitiated: false });
    await dispatch(getOrder(order.id));
    const pjids = order.Placements.map(p => p.pushJobId).filter(pjid => Number.isInteger(pjid));
    const pushJobId =  (pjids.length > 0) ? Math.max.apply(0, pjids) : null;
    if (pushJobId) {
      let pr;
      try {
        pr = await client.get(`/pushjobs/${pushJobId}`);
      } catch (err) {
        toast.error(`Could not load push job ${pushJobId} results`, {
          theme: 'colored',
          toastId: `pushjob-${pushJobId}`
        });
        return;
      }
      const { pushResults } = pr.data;
      if (!pushResults) {
        const errMsg = `Push to GAM had no results back, presumably failed`;
        toast.error(errMsg, { theme: 'colored', toastId: errMsg });
      } else if (pushResults.itemCount === 0) {
        const message = `No placements could be pushed`;
        toast.error(message, { theme: 'colored', toastId: message });
      } else {
        const message = `Pushing ${pushResults.itemCount} to GAM`;
        toast.success(message, { theme: 'colored', toastId: message });
      }
    } else {
      const message = `Push to GAM completed`;
      toast.success(message, { theme: 'colored', toastId: message });
    }
  }

  setValidationRules(orderType, statusId) {
    if (orderType === 'Programmatic') {
      this.orderValidationRules = {
        agencyOrderName: {
          required: { msg: 'Name is required' },
          maxLength: { msg: 'Agency IO Name must be no more than 107 characters', value: 107 }
        },
        IoTypeId: {
          required: { msg: 'IO Type is required' }
        },
        buyerName: {
          required: { msg: 'Buyer Name is required' }
        },
        buyerId: {
          required: { msg: 'Buyer Id is required' }
        },
        creativeSource: {
          required: { msg: 'Creative Source is required' }
        }
      };
    } else if (statusId > 3) {
      this.orderValidationRules = {
        agencyOrderName: {
          required: { msg: 'Name is required' },
          maxLength: { msg: 'Agency IO Name must be no more than 107 characters', value: 107 }
        },
        agencyOrderId: {
          required: { msg: 'Agency IO ID is required' },
          maxLength: { msg: 'Agency IO ID must be no more than 20 characters', value: 20 }
        },
        IoTypeId: {
          required: { msg: 'IO Type is required' }
        },
        IoStatusId: {
          required: { msg: 'IO Status is required' }
        },
        packageType: {
          required: { msg: 'Package Type is a required field' }
        },
        billingType: {
          required: { msg: 'Billing Type is a required field' }
        }
      };
    } else {
      this.orderValidationRules = {
        agencyOrderName: {
          required: { msg: 'Name is required' },
          maxLength: { msg: 'Agency IO Name must be no more than 107 characters', value: 107 }
        },
        IoTypeId: {
          required: { msg: 'IO Type is required' }
        },
        IoStatusId: {
          required: { msg: 'IO Status is required' }
        },
        packageType: {
          required: { msg: 'Package Type is a required field' }
        }
      };
    }
  }

  getAdvertiser = () => {
    const { order, advertiser } = this.props;
    if (order && !isEmpty(order.Advertiser)) {
      return order.Advertiser;
    }
    if (!isEmpty(advertiser)) {
      return advertiser;
    }
    return {};
  };

  static getDerivedStateFromProps(props, state) {
    const updOrder = props.order;
    const curOrder = state.order;
    if (updOrder && curOrder &&
      (!isEqual(updOrder.Placements, curOrder.Placements) || !isEqual(updOrder.Packages, curOrder.Packages))) {
      const order =  {...curOrder, Placements: updOrder.Placements, Packages: updOrder.Packages };
      order.totalOrderAmount = updOrder.totalOrderAmount;
      order.unallocatedPkgs = updOrder.unallocatedPkgs;
      return { order };
    }
    return state;
  }

  setGrid = (event, newValue) => {
    this.setState({ gridType: newValue });
    localStorage.setItem('gridType', newValue);
  };

  validateIo = order => {
    const { errors } = this.state;
    this.setValidationRules(order.orderType, order.IoStatusId);
    Object.keys(this.orderValidationRules).forEach(field => {
      const err = validate(order[field], this.orderValidationRules[field]);
      if (err) {
        errors[field] = err;
      }
    });
    const { itemRemainder, itemLimit } = countLineItems(order);
    if (itemRemainder < 0) {
      errors.IoStatusId = `Change to this status will put order over ${itemLimit} line items`;
    }
    return errors;
  };

  saveIo = (/* evt */) => {
    const { order } = this.state;
    const { dispatch, history } = this.props;
    const errors = this.validateIo(order);
    if (isEmpty(errors)) {
      const id = order.id || 0;
      const pbo = pickBy(order);
      dispatch(saveOrderAndNotify(id, pbo, history));
    } else {
      this.setState({ errors });
    }
  };

  closeCloneDialog = () => {
    this.setState({
      cloneDialogOpen: false,
      cloneIOAgencyId: '',
      cloneIOAgencyName:  ''
    });
  };

  handleCloneOrder = (/* evt */) => {
    this.props.onClone(this.state.cloneIOAgencyName, this.state.cloneIOAgencyId, this.state.cloneIOAdvertiser.id);
    this.closeCloneDialog();
  };

  handleCloneIOChange = (evt) => {
    const newState = {};
    newState[evt.target.id] = evt.target.value;
    this.setState(newState);
  };

  handleAdvertiserChange = (advertiser) => {
    const newState = {};
    newState['cloneIOAdvertiser'] = advertiser;
    this.setState(newState);
  };

  handleSelect = item => {
    const { order, errors } = this.state;
    order.buyerName = item.name;
    order.buyerId = item.buyeraccountid;
    this.setState({ order, errors });
  };

  handleChange = evt => {
    const { order, errors } = this.state;
    const { name, value } = evt.target;
    const err = validate(value, this.orderValidationRules[name]);
    if (err) {
      errors[name] = err;
    } else {
      delete errors[name];
    }
    if (name === 'agencyOrderId' && order.jagIoGroup === order.agencyOrderId) {
      order.jagIoGroup = value;
    }
    order[name] = value;
    this.setState({ order, errors });
  };

  requestAcceptance = () => {
    const { order, dispatch } = this.props;
    dispatch(requestAcceptanceWithNotifications(order.id)).then(() => {
      this.updateProposalStatus(this.props.accept);
    });
  };

  reopenProposal = () => {
    const { order, dispatch } = this.props;
    dispatch(reopenProposalWithNotifications(order.id)).then(() => dispatch(getOrder(order.id)));
  };

  changeBuyer = buyer => {
    const { order, dispatch, history } = this.props;
    dispatch(changeBuyerWithNotifications(order.id, JSON.stringify(buyer), history));
  };

  updateProposalStatus = (accept) => {
    if (!accept || !accept.proposalStatus) return;
    const { order } = this.state;
    const newOrder = Object.assign({}, order);
    newOrder.dfpProposalStatus = accept.proposalStatus;
    this.setState({ order: newOrder });
  };

  validStatusList = () => {
    const { order, constants } = this.props;
    if (!constants) return [];
    if (!order || !order.IoStatusId) {
      return constants.ioStatuses;
    }
    return constants.ioStatuses.filter(s => s.id >= order.IoStatusId);
  };

  goBack = () => {
    const { history } = this.props;
    const { order } = this.state;
    history.push(`/advertiser/${order.AdvertiserId}`)
  };

  canDelete = () => {
    const { order } = this.state;
    return (order && !order.dfpOrderId && !order.dfpProposalId);
  };

  /**
   * Control programmatic workflow based on order fields.
   * This is tricky since a proposal can be reopened.
   * You can send to the buyer if it's a proposal with some proposal line items
   * that are either not finalized or finalized and reopened.
   * You can change the buyer if it's been sent to the buyer but was never finalized.
   * If it is finalized, you can reopen it.
   * @param order
   * @returns {string}
   */
  setProgrammaticState = order => {
    let programmaticState = 'NotAvailable';
    if (order.orderType==='Programmatic') {
      if (order.dfpProposalId && order.buyerId &&
        (order.dfpProposalStatus === 'DRAFT' || order.dfpReopenStatus === 'Reopened') &&
        order.Placements && order.Placements.some(p => p.dfpProposalLineItemId)) {
        programmaticState = 'DraftOrReopened';
      } else if (order.dfpProposalStatus === 'SENT TO BUYER' && !order.dfpOrderId) {
        programmaticState = 'SentToBuyer';
      } else if (order.dfpProposalStatus === 'FINALIZED') {
        programmaticState = 'Finalized';
      }
    }
    return programmaticState;
  };

  /**
   * This will attempt to find an occurrence of 'Error.'
   * and then parses out a GAM error of the form ErrorType.SPECIFIC_ERROR
   * e.g. LineItemError.UPDATE_ARCHIVED_LINE_ITEM_NOT_ALLOWED
   * Specific error scans forward to first character not alphabetic upper case or _
   * Error type scans backward until reaching a non-alphabetic character, then is reversed.
   * @param errorString from GAM
   * @returns {string} parsed error
   */
  parseError = errorString => {
    if (!errorString) {
      return 'No error found';
    }
    let errorStartIx = errorString.indexOf("Error.");
    if (errorStartIx === -1) {
      return 'Cannot parse error';
    }
    errorStartIx += 6;  // length of string "Error."
    const errorEndIx = errorStartIx + errorString.substring(errorStartIx).search(/[^_ABCDEFGHIJKLMNOPQRSTUVWXYZ]/);
    const actualError = errorString.slice(errorStartIx, errorEndIx);
    let errorTypeRev = '';
    let ch = errorStartIx - 2;
    while (errorString[ch].toLowerCase() !== errorString[ch].toUpperCase()) {
      errorTypeRev += errorString[ch];
      ch--;
    }
    const errorType = errorTypeRev.split("").reverse().join("");
    return `${errorType}.${actualError}`
  }

  onOrdersToggle = expanded => {
    this.setState({ ordersExpanded: true });
  }

  onPackagesToggle = expanded => {
    this.setState({ packagesExpanded: expanded})
  };

  onNotifyUpload = ids => {
    this.setState({ pushInitiated: true });
  };

  render() {
    const {
      buyers,
      constants,
      geos,
      strategies,
      deviceTypes,
      salesProducts,
      creativeProducts,
      presets,
      classes,
      onSave,
      onExport,
      onImport,
      importErrors,
      toggleChangeLog,
      toggleAttachments,
      togglePushSettings,
      importExportOpen,
      toggleImportExport,
      onTemplateDownload,
      onPushOrder,
      onPushPackages,
      pushPlacementsFromOrder,
      cannotPushReason,
      itemRemainder,
      isReadOnly,
      amPushRules,
      dispatch,
      setCloneDialog,
      setCloneDialogFalse,
      cloneDialogOpen,
      setIOSelected,
      setPushData,
      ...other
    } = this.props;
    let { error } = this.props;

    const {
      advertiser,
      order,
      errors,
      cloneIOAgencyName,
      cloneIOAgencyId,
      pushInitiated,
      gridType,
      orderExpanded,
      cloneIOAdvertiser,
      user
    } = this.state;

    if (isEmpty(error) && order && order.error === 'no such order') {
      error = Object.create({ response: { status: 404 } });
    }
    const programmaticState = this.setProgrammaticState(order);
    const isLocked = Boolean(order.lock) || pushInitiated;
    if (isLocked) {
      this.pollLockStatus().then(r => console.log('order is locked'));
    }
    const isDev = (constants && constants.whitelistedDevs.includes(user));

    return(
      <div>
        <TrackingCtx.Provider value={{ category: "Order", action:"IO Page", label: `IO#${order.id || ''}` }}>
        {cloneDialogOpen && (<CloneIoDialog
          advertiser={cloneIOAdvertiser.name}
          onCancelClone={this.closeCloneDialog}
          onCloneOrder={this.handleCloneOrder}
          onCloneIOChange={this.handleCloneIOChange}
          onAdvertiserChange={this.handleAdvertiserChange}
          cloneIOAgencyName={cloneIOAgencyName}
          cloneIOAgencyId={cloneIOAgencyId}
          orderType={order.orderType}
          setCloneDialogFalse={setCloneDialogFalse}
        />
        )}

        <ErrorPage error={error} entity="order">
          <IoToolbar
            onSave={this.saveIo}
            isNew={!order.id}
            isLocked={isLocked}
            onPushOrder={onPushOrder}
            onPushPackages={onPushPackages}
            cannotPushReason={cannotPushReason}
            onBack={this.goBack}
            cloneDialogOpen={this.setCloneDialog}
            setCloneDialog={setCloneDialog}
            canClone={true}
            onConfigPush={this.onHandlePush}
            onExport={onExport}
            onImport={onImport}
            canImportExport={true}
            importErrors={importErrors}
            onSelect={this.handleSelect}
            canDelete={this.canDelete()}
            toggleChangeLog={toggleChangeLog}
            togglePushSettings={togglePushSettings}
            toggleAttachments={toggleAttachments}
            importExportOpen={importExportOpen}
            toggleImportExport={toggleImportExport}
            onTemplateDownload={onTemplateDownload}
            onRequestAcceptance={this.requestAcceptance}
            onRetract={this.retractProposal}
            onReopen={this.reopenProposal}
            onChangeBuyer={this.changeBuyer}
            onChange={this.handleChange}
            constants={constants}
            strategies={strategies}
            deviceTypes={deviceTypes}
            salesProducts={salesProducts}
            creativeProducts={creativeProducts}
            presets={presets}
            programmaticState={programmaticState}
            isReadOnly={isReadOnly}
            dispatch={dispatch}
            geos={geos}
            {...other}
          />
          <div className={classes.root}>
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Card>
                  <IoForm
                    isLive={isLiveOrder(order.Placements)}
                    statusList={this.validStatusList(order)}
                    order={order}
                    errors={errors}
                    advertiser={advertiser}
                    buyers={buyers}
                    constants={constants}
                    orderType={order.orderType}
                    onChange={this.handleChange}
                    onSelect={this.handleSelect}
                    itemRemainder={itemRemainder}
                    expanded={orderExpanded}
                    isLocked={isLocked}
                    onPushPackages={onPushPackages}
                    onToggleExpand={expanded => this.onOrdersToggle(expanded)}
                    togglePushSettings={togglePushSettings}
                  />
                </Card>
              </Grid>
              {order && order.id && (
                <Tabs
                  value={gridType}
                  onChange={this.setGrid}
                  textColor="primary"
                  indicatorColor="primary"
                  aria-label="choose grid type"
                >
                  {advertiser.type !== 'Programmatic' && (
                    <Tab label="Packages" />
                  )}
                  <Tab label="Placements" />
                  {isDev && (<Tab label="Latest Push Results" />)}
                </Tabs>
              )}
              {isDev && order && order.id && order.Pushjobs && gridType === 2 && (
                <Grid container spacing={1} sx={{ overflowY: "scroll", maxHeight: "650px"}}>
                  <Grid item xs={12}>
                    <a href='https://developers.google.com/ad-manager/api/reference/v202305/LineItemService#ApiVersionError'>GAM error reference</a>
                    {order.Pushjobs.map ((j) =>
                      <Card variant={'outlined'}>
                        <CardContent>
                          <p><b>Placement IDs:</b> {JSON.stringify(j.placementIds)}</p>
                          <p><b>Push Results:</b> {JSON.stringify(j.pushResults)}</p>
                          <p><b>Parsed GAM Error:</b> {this.parseError(j.bigError)}</p>
                          <p><b>Full GAM Error:</b> {j.bigError}</p>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                </Grid>
              )}
              {order && order.id && gridType === 0 && advertiser.type !== 'Programmatic' && (
                <Grid item xs={12}>
                  <Card>
                    <PackagesGrid
                      order={order}
                      isLocked={isLocked}
                      onNotifyUpload={ids => this.onNotifyUpload(ids)}
                      isReadOnly={isReadOnly}
                      amPushRules={amPushRules}
                      togglePushSettings={togglePushSettings}
                      setIOSelected={setIOSelected}
                      onPushPackages={onPushPackages}
                      whitelist={constants.whitelistedDevs}
                      setPushData={setPushData}
                    />
                  </Card>
                </Grid>
              )}
              {order && order.id && (gridType === 1 || advertiser.type === 'Programmatic') && (
                <Grid item xs={12}>
                  <Card>
                    <PlacementsGrid
                      order={order}
                      isLocked={isLocked}
                      onNotifyUpload={ids => this.onNotifyUpload(ids)}
                      isReadOnly={isReadOnly}
                      amPushRules={amPushRules}
                      togglePushSettings={togglePushSettings}
                      setIOSelected={setIOSelected}
                      pushPlacementsFromOrder={pushPlacementsFromOrder}
                      whitelist={constants.whitelistedDevs}
                      setPushData={setPushData}
                    />
                  </Card>
                </Grid>
              )}
            </Grid>
          </div>
        </ErrorPage>
        </TrackingCtx.Provider>
      </div>
    )
  }
}

IoPage.defaultProps = {
  order: null,
  buyers: null
};

IoPage.propTypes = {
  order: PropTypes.object,
  buyers: PropTypes.array
};

export default withStyles(styles)(IoPage);
